import { useMemo } from 'react'
import { Settings2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { SortableList } from '@/components/common/SortableList'
import { applyColumnPrefs, useColumnPrefs } from './preferences'
import { usePermissionFilteredColumns } from './usePermissionFilteredColumns'
import type { ColumnDef } from './types'

interface ColumnSettingsPopoverProps<TRow> {
  /** Same tableId as DataTable - shared localStorage prefs. */
  tableId: string
  /** Original columns config (before perm filter / order). */
  columns: ColumnDef<TRow>[]
  /**
   * When true, the trigger is a compact icon-only button (used inside the
   * leading meta column header). Default false renders the labelled "Kolumny" button.
   */
  iconOnly?: boolean
}

/**
 * Popover with column visibility checkboxes + DnD reorder + reset.
 * Uses the same useColumnPrefs(tableId) as DataTable, so changes propagate via shared state.
 */
export function ColumnSettingsPopover<TRow>({
  tableId,
  columns,
  iconOnly,
}: ColumnSettingsPopoverProps<TRow>) {
  const permFiltered = usePermissionFilteredColumns(columns)
  const { prefs, reset, toggleHidden, setOrder } = useColumnPrefs(tableId)
  const { ordered } = useMemo(() => applyColumnPrefs(permFiltered, prefs), [permFiltered, prefs])

  return (
    <Popover>
      <PopoverTrigger asChild>
        {iconOnly ? (
          <Button
            variant="ghost"
            size="icon"
            className="size-7 text-muted-foreground hover:text-foreground"
            aria-label="Ustawienia kolumn"
            title="Ustawienia kolumn"
          >
            <Settings2 className="size-4" />
          </Button>
        ) : (
          <Button variant="outline" size="sm" className="gap-2">
            <Settings2 className="size-4" />
            Kolumny
          </Button>
        )}
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0">
        <div className="flex items-center justify-between border-b px-4 py-2">
          <span className="text-sm font-medium">Kolumny tabeli</span>
          <button
            type="button"
            onClick={reset}
            className="text-xs text-muted-foreground hover:text-foreground"
          >
            Zresetuj
          </button>
        </div>
        <div className="max-h-80 overflow-y-auto p-2">
          <SortableList
            items={ordered}
            getId={(c) => c.key}
            onReorder={(keys) => setOrder(keys.map(String))}
            className="flex flex-col gap-1"
            renderItem={(col) => {
              const isHidden = prefs.hidden.includes(col.key)
              return (
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    className="size-4 accent-primary disabled:opacity-50"
                    checked={!isHidden}
                    disabled={col.alwaysVisible}
                    onChange={() => !col.alwaysVisible && toggleHidden(col.key)}
                    aria-label={col.alwaysVisible ? `${col.header} (zawsze widoczna)` : col.header}
                  />
                  <span className="flex-1 text-sm truncate">
                    {col.header || <span className="text-muted-foreground italic">(bez nagłówka)</span>}
                  </span>
                  {col.alwaysVisible && (
                    <span className="text-[10px] text-muted-foreground">zawsze</span>
                  )}
                </div>
              )
            }}
          />
        </div>
      </PopoverContent>
    </Popover>
  )
}
